/**
 * When the race button may send (2026-11-03 race timing review). Pure, so the
 * rules `useAotdRace` applies to a tap can be tested on their own.
 */

/**
 * Backend burst limiter near unlock (backend `middleware/aotdRateLimit.ts`): a
 * third request inside 1s, counted by server arrival, earns a 4.5s ban.
 */
export const SERVER_BURST_MAX = 2;
export const SERVER_BURST_WINDOW_MS = 1_000;
const BURST_MARGIN_MS = 50;
// A request still in flight is assumed to reach the server at most this long
// after it left (or now, if later).
const IN_FLIGHT_ARRIVAL_MS = 300;

/** One of our /today requests: when it left (Date.now()) and its round trip once answered. */
export interface SentRequest {
  sentAtMs: number;
  rttMs: number | null;
}

/**
 * Once unlock has passed: true when the latest /today can't have entered the
 * race, so the next tap may go at once (no cooldown, no waiting for it).
 * Answered `not_started` or `entered_early`, it was turned away; a failed one
 * counts if it left before unlock. Still in flight (`verdict` null) and sent
 * before unlock, it is either turned away or, inside the 25ms tolerance,
 * already counted at the unlock instant; a repeat then races with the stored
 * time, so it costs nothing.
 */
export function requestCouldNotEnter(args: {
  unlockMs: number | null;
  serverNowMs: number;
  sentServerMs: number | null;
  verdict: string | null;
}): boolean {
  const { unlockMs, serverNowMs, sentServerMs, verdict } = args;
  if (unlockMs === null || serverNowMs < unlockMs) return false;
  const sentBeforeUnlock = sentServerMs !== null && sentServerMs < unlockMs;
  if (verdict === null) return sentBeforeUnlock;
  if (verdict === "not_started" || verdict === "entered_early") return true;
  return verdict === "error" && sentBeforeUnlock;
}

/**
 * How long the next /today must wait so it isn't the third inside the burst
 * window. The limiter counts arrivals, and a request can reach the server well
 * after it left (the page-load one may pay a CORS preflight and a handshake
 * first), so each earlier request is placed at the latest it can have arrived:
 * before its answer came back, i.e. `sentAt + rtt`.
 */
export function burstWaitMs(sent: SentRequest[], nowMs: number): number {
  const arrivedBy = sent
    .map((r) => (r.rttMs !== null ? r.sentAtMs + r.rttMs : Math.max(nowMs, r.sentAtMs + IN_FLIGHT_ARRIVAL_MS)))
    .sort((a, b) => b - a);
  if (arrivedBy.length < SERVER_BURST_MAX) return 0;
  const olderOfLastTwo = arrivedBy[SERVER_BURST_MAX - 1];
  return Math.max(0, olderOfLastTwo + SERVER_BURST_WINDOW_MS + BURST_MARGIN_MS - nowMs);
}

// A page that hasn't loaded the previous art yet holds its /info and image
// from 5s before to 2s after unlock.
export const ART_HOLD_BEFORE_UNLOCK_MS = 5_000;
export const ART_HOLD_AFTER_UNLOCK_MS = 2_000;

/** True inside the seconds around unlock when the previous art's downloads wait. */
export function inArtHoldWindow(msToUnlock: number | null): boolean {
  return (
    msToUnlock !== null &&
    msToUnlock < ART_HOLD_BEFORE_UNLOCK_MS &&
    msToUnlock > -ART_HOLD_AFTER_UNLOCK_MS
  );
}
